import React from 'react';
import { Activity, Shield, Cloud, Headphones } from 'lucide-react';
import { statistics } from '../../data/mock';
import useScrollAnimation from '../../hooks/useScrollAnimation';

const iconMap = {
  Activity,
  Shield,
  Cloud,
  Headphones,
};

const Statistics = () => {
  const { ref: sectionRef, isVisible } = useScrollAnimation();
  
  return (
    <section ref={sectionRef} className="py-20 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#1A1512] via-[#251E18] to-[#1A1512]" />
      
      {/* Diagonal Pattern */}
      <div className="absolute inset-0 diagonal-pattern opacity-30" />
      
      {/* Gold Line Top with diamond */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-center">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#C9A97B]/30 to-[#C9A97B]/30" />
        <div className="w-2 h-2 rotate-45 bg-[#C9A97B]/30 mx-2" />
        <div className="flex-1 h-px bg-gradient-to-l from-transparent via-[#C9A97B]/30 to-[#C9A97B]/30" />
      </div>
      
      {/* Gold Line Bottom with diamond */}
      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#C9A97B]/30 to-[#C9A97B]/30" />
        <div className="w-2 h-2 rotate-45 bg-[#C9A97B]/30 mx-2" />
        <div className="flex-1 h-px bg-gradient-to-l from-transparent via-[#C9A97B]/30 to-[#C9A97B]/30" />
      </div>

      {/* Corner accents */}
      <div className="absolute top-4 left-4 w-16 h-16 border-l border-t border-[#C9A97B]/10 rounded-tl-lg hidden lg:block" />
      <div className="absolute top-4 right-4 w-16 h-16 border-r border-t border-[#C9A97B]/10 rounded-tr-lg hidden lg:block" />
      <div className="absolute bottom-4 left-4 w-16 h-16 border-l border-b border-[#C9A97B]/10 rounded-bl-lg hidden lg:block" />
      <div className="absolute bottom-4 right-4 w-16 h-16 border-r border-b border-[#C9A97B]/10 rounded-br-lg hidden lg:block" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
          {statistics.map((stat, index) => {
            const IconComponent = iconMap[stat.icon];
            return (
              <div
                key={stat.id}
                className="text-center group relative"
              >
                {/* Connecting lines between stats - desktop only */}
                {index < statistics.length - 1 && (
                  <div className="absolute top-1/2 -right-2 w-4 h-px bg-[#C9A97B]/20 hidden lg:block" />
                )}
                
                {/* Icon with animated border */}
                <div 
                  className={`relative w-16 h-16 mx-auto mb-4 ${isVisible ? 'animate-bounceIn' : 'opacity-0'}`}
                  style={{ animationDelay: `${index * 150}ms`, animationFillMode: 'forwards' }}
                >
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#C9A97B]/20 to-[#C9A97B]/5 group-hover:from-[#C9A97B]/30 group-hover:to-[#C9A97B]/10 transition-all duration-500" />
                  <div className="absolute inset-0 rounded-2xl border border-[#C9A97B]/10 group-hover:border-[#C9A97B]/30 transition-colors duration-500" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <IconComponent className="w-8 h-8 text-[#C9A97B] group-hover:scale-110 transition-transform duration-500" />
                  </div>
                </div>

                {/* Value with animated underline */}
                <div 
                  className={`relative inline-block ${isVisible ? 'animate-zoomIn' : 'opacity-0'}`}
                  style={{ animationDelay: `${index * 150 + 200}ms`, animationFillMode: 'forwards' }}
                >
                  <div className="text-3xl lg:text-4xl font-display font-bold text-gold-gradient mb-2">
                    {stat.value}
                  </div>
                </div>

                {/* Label */}
                <div 
                  className={`text-[#BFB5A5] text-sm lg:text-base ${isVisible ? 'animate-fadeIn' : 'opacity-0'}`}
                  style={{ animationDelay: `${index * 150 + 350}ms`, animationFillMode: 'forwards' }}
                >
                  {stat.label}
                </div>

                {/* Decorative Line with dots */}
                <div 
                  className={`mt-4 flex items-center justify-center gap-1 ${isVisible ? 'animate-expandWidth' : 'opacity-0'}`}
                  style={{ animationDelay: `${index * 150 + 450}ms`, animationFillMode: 'forwards' }}
                >
                  <div className="w-1 h-1 rounded-full bg-[#C9A97B]/30" />
                  <div className="h-0.5 w-8 bg-gradient-to-r from-[#C9A97B]/30 via-[#C9A97B]/50 to-[#C9A97B]/30 rounded-full" />
                  <div className="w-1 h-1 rounded-full bg-[#C9A97B]/30" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Statistics;
